import {
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListPreferencesDocument,
  PreferenceTargetType,
} from '../../api/graphql/generated';
import {
  UserPreferences,
  isOfTypeUserPreferenceKey,
  useSetUserPreferenceLazy,
  useUserPreferencesByKey,
} from '../../api/hooks/preference.hooks';
import { useCurrentUserProp } from '../../api/user.hooks';
import { SystemUnit } from '../../scratchpad/application/shared/model/flow.types';
import { useHasForcedColorScheme } from '../branding/branding.hooks';
import { SaveStatusToast } from '../toast/toast.gql';

const UserPreferenceList = (): ReactElement => {
  const { i18n } = useTranslation();
  const preferencesByKey = useUserPreferencesByKey();
  const [preferences, setPreferences] = useState(preferencesByKey);
  const [setUserPreference] = useSetUserPreferenceLazy();
  const userId = useCurrentUserProp<string>('id');

  useEffect(() => {
    setPreferences(preferencesByKey);
  }, [preferencesByKey]);

  const [saveError, setSaveError] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const savePreference = useCallback(
    async (key: string, value: unknown) => {
      const preferenceKey = key.split('.')[1];

      if (!isOfTypeUserPreferenceKey(preferenceKey)) {
        return;
      }

      const previousValue = preferencesByKey[preferenceKey];

      setPreferences({
        ...preferences,
        [preferenceKey]: value,
      });

      setSaveLoading(true);

      const res = await setUserPreference(
        {
          key,
          value,
        },
        {
          refetchQueries: [
            {
              query: ListPreferencesDocument,
              variables: {
                input: {
                  targetType: PreferenceTargetType.USER,
                  targetId: userId as string,
                  prefix: 'user',
                },
              },
            },
          ],
        }
      );

      if (res?.errors) {
        setSaveError(true);
        setPreferences({
          ...preferences,
          [preferenceKey]: previousValue,
        });
      }

      setSaveLoading(false);
    },
    [preferences, preferencesByKey, setUserPreference, userId]
  );

  const hasForcedColorScheme = useHasForcedColorScheme();

  return (
    <IonList lines="full">
      <IonListHeader>
        {i18n.t('common:user_preference_preferences')}
      </IonListHeader>
      <IonItem>
        <IonSelect
          interface="popover"
          onIonChange={(e) => {
            savePreference(UserPreferences.USER_LANGUAGE, e.detail.value);
          }}
          label={i18n.t('common:user_preference_language')}
          value={preferences.language}
        >
          <IonSelectOption value="system">
            {i18n.t('common:user_preference_system')}
          </IonSelectOption>
          <IonSelectOption value="en">
            {i18n.t('common:user_preference_english')}
          </IonSelectOption>
          <IonSelectOption value="fr">
            {i18n.t('common:user_preference_french')}
          </IonSelectOption>
          <IonSelectOption value="es">
            {i18n.t('common:user_preference_spanish')}
          </IonSelectOption>
        </IonSelect>
      </IonItem>
      {!hasForcedColorScheme && (
        <IonItem>
          <IonSelect
            interface="popover"
            onIonChange={(e) =>
              savePreference(UserPreferences.USER_THEME, e.detail.value)
            }
            label={i18n.t('common:user_preference_theme')}
            value={preferences.theme}
          >
            <IonSelectOption value="system">
              {i18n.t('common:user_preference_system')}
            </IonSelectOption>
            <IonSelectOption value="dark">
              {i18n.t('common:user_preference_dark')}
            </IonSelectOption>
            <IonSelectOption value="light">
              {i18n.t('common:user_preference_light')}
            </IonSelectOption>
          </IonSelect>
        </IonItem>
      )}
      <IonItem>
        <IonSelect
          aria-label="uom"
          interface="popover"
          onIonChange={(e) =>
            savePreference(UserPreferences.UNIT_OF_MEASURE, e.detail.value)
          }
          // default to imperial
          value={preferences.unit_of_measure ?? SystemUnit.IMPERIAL}
        >
          <div slot="label">
            <IonLabel>{i18n.t('common:unit_of_measure')}</IonLabel>
          </div>
          <IonSelectOption value={SystemUnit.IMPERIAL}>
            {i18n.t('common:imperial')}
          </IonSelectOption>
          <IonSelectOption value={SystemUnit.METRIC}>
            {i18n.t('common:metric')}
          </IonSelectOption>
        </IonSelect>
      </IonItem>
      <SaveStatusToast loading={saveLoading} error={saveError} />
    </IonList>
  );
};

export { UserPreferenceList };
